import { type InputHTMLAttributes, forwardRef, useCallback } from "react";
import { Calendar, X } from "lucide-react";
import { dateLabel } from "../lib/finance";

export interface DateInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  placeholder?: string;
  onClear?: () => void;
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  {
    value,
    placeholder = "Select date",
    className = "",
    disabled,
    required,
    onChange,
    onClear,
    ...props
  },
  ref,
) {
  const strVal = typeof value === "string" ? value : "";
  let displayText = placeholder;
  const isFilled = Boolean(strVal);

  if (isFilled) {
    try {
      displayText = dateLabel(strVal);
    } catch {
      displayText = strVal;
    }
  }

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (onClear) {
        onClear();
      } else if (onChange) {
        const syntheticEvent = {
          target: { value: "" },
          currentTarget: { value: "" },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    },
    [onChange, onClear],
  );

  return (
    <div
      className={`date-input-wrap ${isFilled ? "is-filled" : "is-empty"} ${disabled ? "is-disabled" : ""} ${className}`.trim()}
    >
      <span
        className="date-input-display"
        data-date={displayText}
        aria-hidden="true"
      />
      <div className="date-input-actions">
        {isFilled && !required && !disabled && (
          <button
            type="button"
            className="date-input-clear"
            onClick={handleClear}
            aria-label="Clear date"
            tabIndex={-1}
          >
            <X size={14} />
          </button>
        )}
        <Calendar className="date-input-icon" size={16} aria-hidden="true" />
      </div>
      <input
        ref={ref}
        type="date"
        lang="en-GB"
        value={value}
        disabled={disabled}
        required={required}
        onChange={onChange}
        className="date-input-native"
        {...props}
      />
    </div>
  );
});

export default DateInput;
